/**
 * 슬라임 경주 (DESIGN 8.6).
 *
 * 이 화면이 반드시 말해야 하는 것 셋:
 *   ① **얼마를 걸면 얼마를 받나** — 배당은 실시간으로 변한다. 그게 파리뮤추얼의 재미다
 *   ② **무엇을 보고 고를까** — 회차 · 말별 예상 배당 · 컨디션 다섯 눈금
 *   ③ **지금 어디까지 왔나** — 남은 시간과 달리는 그림
 *
 * ⛔ 예전에 여기 있던 "조작할 수 없는 이유"(해시·시드)와 "기대값은 마이너스다" 는
 *   **지웠다.** 사용자: *"이따위 내용 없애.. 유저 기만이야.. 유저에겐 희망을 팔아야지"*.
 *   공정성 장치(커밋-리빌)는 서버에 그대로 있다 — 화면에서 안 그릴 뿐이다.
 */
import { S } from '../../core/state.js';
import {
  BET_MIN, HOUSE_EDGE, WIN_BONUS_RATE, RACE_RUN,
  SLIMES, SLIME_COL, SLIME_NAMES, CONDS, COND_COL, carryRelease, odds, payout, positions,
} from '../../world/race.js';
import { betQuick, chanceNotice, fold, muted, sec, npcHead, maxBeforeTax, taxPct, withTax } from './common.js';

const num = (n) => Number(n || 0).toLocaleString();
/** UNI-Q190 — 판돈에는 왕도 세금이 더해진다. 한 번 누를 때 실제로 나가는 은화를 입력과 함께 바꿔 보인다 */
const payText = (amount) => amount >= BET_MIN
  ? `한 번 누르면 ${num(withTax(amount))} 은화가 나간다 (판돈 ${num(amount)} + 왕도 세금 ${taxPct()}%)`
  : `금액을 정하고 슬라임을 누른다 · 왕도 세금 ${taxPct()}%가 더해진다`;
/**
 * 말마다의 베팅 버튼 — 실제로 나가는 은화(판돈 + 왕도 세금)를 적는다(UNI-Q200 · 사용자 「이것도 표식에 세금 포함으로 표기를
 * 해야지」: 블랙잭·경주 버튼이 세금 전 판돈만 적었다. 슬롯은 처음부터 세금 포함).
 */
const betLabel = (amount, on) => amount >= BET_MIN ? `${on ? '추가' : '베팅'} · ${num(withTax(amount))}` : (on ? '추가 베팅' : '베팅');
let draftRace = 0;

const clampBet = (n) => {
  const v = Math.floor(Number(n) || 0);
  return Number.isSafeInteger(v) ? Math.max(0, v) : 0;
};

/** HN 이전 한 장 표도 여섯 칸으로 읽는다. */
function picksOf(bet) {
  const out = new Array(SLIMES).fill(0);
  if (Array.isArray(bet?.picks)) for (let i = 0; i < SLIMES; i++) out[i] = clampBet(bet.picks[i]);
  if (!out.some(Boolean) && Number.isInteger(bet?.slime) && bet.slime >= 0 && bet.slime < SLIMES) {
    out[bet.slime] = clampBet(bet.amount);
  }
  return out;
}

/** 지금 입력한 표까지 풀에 더했을 때의 정확한 서버 미러 예상 상금. */
function projected(r, slime, amount, mine = []) {
  if (amount < BET_MIN) return 0;
  const pool = r.pool.slice();
  pool[slime] = (pool[slime] || 0) + amount;
  return payout(pool, slime, (mine[slime] || 0) + amount, r.carry || 0, r.useMultiplier);
}

function previewText(r, slime, amount, mine) {
  // 2026-10-21 — 금액이 없을 때의 안내는 판돈 줄(payText)에 한 번만 — 여섯 줄에 같은 문장을 되풀이하지 않는다
  if (amount < BET_MIN) return '';
  const stake = (mine[slime] || 0) + amount;
  const got = projected(r, slime, amount, mine);
  return `더 걸면 ${num(got)} 은화 예상 · ×${(got / stake).toFixed(2)}`;
}

/** 입력칸과 여섯 예상 상금만 바꾼다. 패널 전체를 갈아치워 클릭을 잃지 않는다. */
function syncDraft() {
  const r = S.race;
  const amount = clampBet(S.raceBetAmount);
  const input = document.querySelector('#betamt');
  if (input && Number(input.value) !== amount) input.value = amount || '';
  const pay = document.querySelector('[data-race-pay]');
  if (pay) pay.textContent = payText(amount);
  if (!r) return;
  // 금액이 없으면 예상 칸이 비므로 [베팅]을 이름 줄 옆에 붙인다(index.html .raceentry.nodraft) — 다시 그리지 않고 칸만 바꾼다
  document.querySelectorAll('#pbody .raceentry').forEach((el) => el.classList.toggle('nodraft', amount < BET_MIN && r.phase === 'bet'));
  const bet = S.you?.inv?.bet?.race === r.id ? S.you.inv.bet : null;
  const mine = picksOf(bet);
  for (let i = 0; i < SLIMES; i++) {
    const el = document.querySelector(`[data-race-preview="${i}"]`);
    if (el) el.textContent = previewText(r, i, amount, mine);
    const btn = document.querySelector(`#pbody button[onclick="act.bet(${i})"]`);
    if (btn) btn.textContent = betLabel(amount, (mine[i] || 0) > 0);
  }
}

export function setBetAmount(n) {
  S.raceBetAmount = clampBet(n);
  syncDraft();
}

export function addBetAmount(n) {
  S.raceBetAmount = clampBet((S.raceBetAmount || 0) + Number(n || 0));
  syncDraft();
}

/** 남은 초 — 서버가 준 `until`(epoch 초)로 화면이 스스로 센다 */
function left(r) {
  return Math.max(0, Math.ceil(r.until - Date.now() / 1000));
}

/** 0~1 로 흐르는 경주 진행도. 초가 아니라 **소수**라야 그림이 부드럽다 */
function progress(r) {
  return Math.max(0, Math.min(1, 1 - (r.until - Date.now() / 1000) / RACE_RUN));
}

/**
 * 남은 초를 **글자만** 갱신한다. 패널을 다시 그리지 않는다.
 *
 * 단계 문구는 저마다 다르므로(마감까지/달리는 중) 각 자리가 자기 문장을 만들고,
 * 여기서는 숫자만 바꾼다 — 문장을 여기 또 적으면 두 벌이 되고 한쪽이 낡는다.
 */
let clockId = 0;
function startClock() {
  if (clockId) return;
  clockId = setInterval(() => {
    /**
     * **멈추는 조건은 "글자가 없다" 가 아니라 "이 화면이 아니다" 다.**
     *
     * 처음엔 글자를 못 찾으면 그 자리에서 타이머를 껐다. 그런데 `render()` 는
     * HTML 을 **돌려주기만** 하고 화면에 넣는 것은 부르는 쪽이라, 첫 틱이 삽입보다
     * 먼저 오면 글자가 아직 없다 — **한 번 어긋나면 그대로 영영 멈췄다.**
     * 실측으로 `마감까지 102초` 가 다섯 번 연속 그대로였다.
     * 글자가 없으면 그냥 이번 틱을 거른다.
     */
    if (!S.race || S.panel !== 'race') { clearInterval(clockId); clockId = 0; return; }
    const el = document.querySelector('#pbody .racesec');
    if (!el) return;
    const r = S.race;
    el.textContent = r.phase === 'bet' ? `마감까지 ${left(r)}초` : `${left(r)}초`;
  }, 250);
}

/**
 * 달리는 그림을 **매 프레임 움직인다.**
 *
 * 사용자: *"요것도 초단위로 새로고침 되도록 해야 자연스럽겠다."* 요구는 "초 단위"
 * 라는 숫자가 아니라 **자연스러움**이다 — 15초짜리 경주인데 화면이 거의 안 움직이면
 * 그건 경주로 안 보인다. 초당 한 번 툭 바뀌는 것도 여전히 끊겨 보인다.
 *
 * **서버는 안 건드린다.** `positions()` 가 (해시·승자·진행도)만 받는 순수 함수라
 * 클라가 자기 시계로 진행도를 흘리면 그것으로 끝난다. 초당 브로드캐스트로 가면
 * 판이 열릴 때마다 비용이 붙고 얻는 게 없다.
 *
 * ⛔ **패널을 다시 그리지 않는다.** 이미 있는 마커의 `style.left` 만 바꾼다.
 *   · 통째로 다시 그리면 재렌더 가드와 싸운다.
 *   · 그리고 **누르고 떼는 사이에 버튼이 교체되면 click 이 아예 안 난다** —
 *     오늘 사용자가 "눌러도 아무 일이 없다" 로 신고한 것과 같은 모양이다.
 *
 * 멈추는 조건을 넉넉히 둔다: 마커가 화면에서 사라졌거나(패널을 닫았다),
 * 판이 바뀌었거나, 달리는 단계가 아니면 그 자리에서 끝낸다.
 */
let animId = 0;
function runTrackAnim(raceId) {
  cancelAnimationFrame(animId);
  const step = () => {
    const r = S.race;
    const lanes = document.querySelectorAll('#pbody .track i');
    if (!lanes.length || !r || r.id !== raceId || r.phase !== 'run') { animId = 0; return; }
    const pos = positions(r.hash, r.winner >= 0 ? r.winner : -1, progress(r));
    for (let i = 0; i < lanes.length && i < pos.length; i++) {
      lanes[i].style.left = `${(pos[i] * 88).toFixed(2)}%`;
    }
    animId = requestAnimationFrame(step);
  };
  animId = requestAnimationFrame(step);
}

export function render({ y, town }) {
  const r = S.race;
  let h = npcHead('race') + chanceNotice();

  if (!town) return h + muted('슬라임 경주는 마을에서 이용할 수 있다.');
  if (!r) return h + muted('<span class="ldspin" aria-hidden="true"></span>다음 경주를 불러오는 중… 오면 저절로 채워진다.');

  const total = r.pool.reduce((a, b) => a + b, 0);
  const carry = Math.max(0, Number(r.carry) || 0);
  if (draftRace !== r.id) { draftRace = r.id; S.raceBetAmount = 0; }
  const draft = clampBet(S.raceBetAmount);
  // 표는 `inv` 에 있다 — 저장되는 자리라야 DO 재시작에도 은화가 안 증발한다
  const bet = (y.inv && y.inv.bet) || null;
  const mine = bet && bet.race === r.id ? bet : null;
  const minePicks = picksOf(mine);
  const selected = minePicks.filter((n) => n > 0).length;
  const available = Math.max(0, Number(y.silver || 0) + Number(S.vault?.silver || 0));
  const secs = left(r);
  const openLane = r.pool.findIndex((n) => Number(n) > 0);
  const releaseNow = openLane >= 0
    ? carryRelease(r.pool, openLane, carry, r.useMultiplier) : 0;

  /**
   * 남은 시간은 **매초 줄어든다.**
   *
   * 패널은 서버가 `race` 를 보낼 때만 다시 그려서, 베팅 단계 120초 내내 `마감까지
   * 120초` 가 **그대로 얼어 있었다.** 마감이 다가오는 것이 안 보이면 서두를 이유가
   * 생기지 않는다 — 판이 열려 있다는 느낌 자체가 안 난다.
   *
   * 글자 하나만 바꾼다(패널을 다시 그리지 않는다). 그림은 rAF 로, 시계는 초 단위로
   * 나눈 이유: 시계는 숨겨진 창에서도 돌아야 다시 봤을 때 맞는 값이고,
   * 그림은 안 보이는 동안 그릴 이유가 없다.
   */
  startClock();

  const fund = `<div class="racepot">
    <span>🏆 경주·복권·블랙잭·슬롯 공동 기금</span><b>${num(carry)} 은화</b>
    <small>이번 판 추가 재원 최대 ${num(releaseNow)} 은화 · 판돈이 늘면 함께 오른다</small>
  </div>`;
  h += fund;

  // ── 단계 ──
  if (r.phase === 'bet') {
    // 2026-10-21 「논문 쓰니?」 — 핵심 한 줄(판돈·적중 최저선)만 보이고 나머지 규칙은 [자세히]
    h += sec(`베팅 <em class="racesec">마감까지 ${secs}초</em>`, fold(
      `이번 판돈 <b>${num(total)}</b> 은화 · 적중하면 최소 <b>원금 +${Math.round(WIN_BONUS_RATE * 100)}%</b>`,
      '이번 판돈과 누적 상금 일부가 함께 배당된다. 여러 슬라임에 여러 번 걸 수 있고, 가진 은화가 곧 한도다. '
      + `패자 판돈의 ${Math.round(HOUSE_EDGE * 100)}%는 다음 누적 상금으로 이어진다. `
      + '정한 금액은 베팅 뒤에도 유지된다 — 같은 슬라임이나 다른 슬라임에 이어서 걸 수 있다. 간편 버튼은 누를 때마다 더해진다.'));
  } else if (r.phase === 'run') {
    // 첫 그림은 소수 진행도로 그린다 — 초 단위로 깎으면 첫 프레임부터 계단이 진다.
    // 그린 **뒤에** 애니메이션을 건다(그 시점에야 마커가 DOM 에 들어와 있다).
    h += sec(`달리는 중 <em class="racesec">${secs}초</em>`, track(r, progress(r)));
    requestAnimationFrame(() => runTrackAnim(r.id));
  } else {
    h += sec('결과', track(r, 1)
      + `<div class="kv"><span>1등</span><b style="color:${SLIME_COL[r.winner]}">${SLIME_NAMES[r.winner]}</b></div>`);
  }
  if (mine) h += `<p class="ux-note">내 제 ${num(r.id)}회 베팅 · ${selected}마리 · 총 ${num(mine.amount)} 은화${r.phase === 'run' ? ' · 달리는 모습을 지켜보자' : ''}</p>`;

  // ── 판돈 ──
  // 말 버튼보다 **먼저** 둔다. 예전엔 여섯 출전표 아래에 입력칸이 있어 모바일에서
  // "건다"를 먼저 보고도 금액을 정할 곳을 못 찾았다. 그 모양이 한 마리만 고르는
  // 선택 화면처럼 읽혔다. 금액을 한 번 정한 뒤 여섯 버튼을 연달아 누르는 흐름이다.
  if (r.phase === 'bet') {
    h += `<div class="sec"><div class="racemulti">
      <b>복수 베팅 · 금액을 정하고 슬라임 선택</b>
      ${selected ? `<em>현재 ${selected}마리 · 총 ${num(mine?.amount)} 은화 베팅 중</em>` : ''}
    </div><div class="row">
      <input id="betamt" type="number" aria-label="슬라임 한 마리에 추가할 판돈 은화" inputmode="numeric" min="${BET_MIN}"
        value="${draft || ''}" oninput="act.betInput(this.value)"
        placeholder="한 번 누를 때 걸 은화 (${num(BET_MIN)} 이상)">
      </div>${betQuick('act.betAdd', `act.betInput(${maxBeforeTax(available, y)})`)}${muted(`<b data-race-pay>${payText(draft)}</b><br>`
        + `가방 <b>${num(y.silver)}</b> + 창고 <b>${num(S.vault?.silver || 0)}</b> = <b>${num(available)}</b> 은화 · `
        + `세금까지 내고 걸 수 있는 판돈 <b>${num(maxBeforeTax(available, y))}</b>`)}</div>`;
  }

  // ── 말 여섯 ──
  // **회차**는 판의 정체성이다. 사용자가 직접 찾아냈다 — "경주가 몇회차인지도 알려주고".
  // 누적이 쌓이는 느낌은 번호에서 나온다.
  const beforeEntries = h; h = '';
  h += `<div class="sec"><h3>제 ${num(r.id)} 회 · 출전</h3>`;
  for (let i = 0; i < SLIMES; i++) {
    const o = odds(r.pool, i, carry, r.useMultiplier);
    const mineAmount = minePicks[i] || 0;
    const on = mineAmount > 0;
    const won = r.phase === 'show' && r.winner === i;
    // 배당은 **예상치다.** 마감 직전에 판이 바뀌면 값이 달라진다 — 그걸 화면이 말한다
    const oddsTxt = o > 0 ? `×${o.toFixed(2)}` : '베팅 없음';
    /**
     * **컨디션은 읽을 거리지 답이 아니다.**
     *
     * 서버가 1등을 뽑은 **뒤에** 만든다 — 원인이 아니라 증거다. 실측으로
     * 최상이 26.5%, 최하가 9.0% 로 이긴다(기준선 16.7%). **최고 컨디션이 지는 판이
     * 73.5%** 라 사용자가 못박은 *"최상이라고 늘 이기는 게 아니다"* 가 성립한다.
     *
     * 배당과 **나란히** 둔다. 기대값 문구를 지운 자리를 채우는 것이 이 둘이고,
     * 둘이 같이 보여야 "어디에 걸까" 가 판단이 된다 — 하나만으로는 그냥 숫자다.
     */
    const c = Array.isArray(r.cond) ? r.cond[i] : null;
    const condTxt = c === null || c === undefined ? ''
      : ` · <b style="color:${COND_COL[c]}">${CONDS[c]}</b>`;
    /**
     * **누적 승률 — 이 세계에서 이 말이 몇 번 이겼나.**
     *
     * 서버는 **횟수만** 보낸다. 나누는 것은 화면 몫이다 — 서버가 미리 나누면
     * "0판 중 0승" 을 어떻게 적을지가 서버 문제가 되기 때문이다.
     *
     * **`0%` 는 쓰지 않는다.** 아직 한 판도 안 돈 것과 *"걸었는데 한 번도 못 이겼다"*
     * 가 같은 글자가 되면 그건 정보가 아니라 오해다. 판이 없으면 `첫 출전` 이라고 적는다.
     *
     * 세계 수명이 72시간이라 세계가 바뀌면 이 값도 리셋된다 — 위의 `제 N 회` 와
     * 같은 눈금 위에 있다("이 세계에서의 누적").
     */
    const winTxt = (() => {
      const ws = Array.isArray(r.wins) ? r.wins : null;
      if (!ws) return '';
      const done = ws.reduce((a, b) => a + (b || 0), 0);
      if (!done) return ' · <span class="qt">첫 출전</span>';
      return ` · <span class="qt">${ws[i] || 0}승 ${Math.round(((ws[i] || 0) / done) * 100)}%</span>`;
    })();
    h += `<div class="item raceentry ${on ? 'sel' : ''}${r.phase === 'bet' && draft < BET_MIN ? ' nodraft' : ''}">
      <span class="nm" style="color:${SLIME_COL[i]}">${won ? '★ ' : ''}${SLIME_NAMES[i]}
        <span class="qt">${num(r.pool[i])} 은화 · 예상 ${oddsTxt}${condTxt}${winTxt}</span></span>
      ${r.phase === 'bet'
        ? `<div class="racepick"><span class="qt" data-race-preview="${i}">${previewText(r, i, draft, minePicks)}</span>
          <button onclick="act.bet(${i})">${betLabel(draft, on)}</button></div>`
        : on ? `<span class="qt">내 판돈 · ${num(mineAmount)}</span>` : ''}
      ${on && r.phase === 'bet' ? `<span class="qt">내 판돈 ${num(mineAmount)}</span>` : ''}
    </div>`;
  }
  h += `</div>`;
  const entries = h; h = beforeEntries;
  if (mine) {
    const rows = minePicks.map((n, i) => {
      if (n <= 0) return '';
      const got = payout(r.pool, i, n, carry, r.useMultiplier);
      return `<div class="kv"><span>${SLIME_NAMES[i]}</span>`
        + `<b>${num(n)} 은화 · 적중 예상 ${num(got)} · ×${(got / n).toFixed(2)}</b></div>`;
    }).join('');
    h += sec(`내 베팅 · 총 ${num(mine.amount)}`, rows);
  }
  h += r.phase === 'bet' ? entries : fold('출전 슬라임 · 컨디션과 배당', entries);

  /**
   * ── 여기 있던 설명 둘을 지웠다 ──────────────────────────────────
   *
   * 사용자가 스크린샷을 찍어 보내며: ***"이따위 내용 없애.. 유저 기만이야..
   * 유저에겐 희망을 팔아야지"***. 그 전에도 한 번 말했었다 — *"해시는 왜 굳이
   * 넣어서 직접 해싱해보라는 식으로… 가려."*
   *
   * 지운 것은 **화면의 설명**이지 공정성 장치가 아니다.
   *   · `조작할 수 없는 이유` — 해시·시드를 늘어놓고 직접 해싱해 보라고 했다.
   *     증명이 필요한 것은 맞지만 **베팅 화면에서 유저에게 숙제를 내는 자리**가 아니다.
   *   · `기대값은 항상 마이너스다 / 사냥보다 이득일 수 없게 만들어져 있다` —
   *     걸기 직전에 "너는 손해다" 라고 말하는 화면이다.
   *
   * ⛔ **서버 커밋-리빌 구조는 그대로다.** `hash` 는 여전히 베팅 전에 공개되고
   * 1등은 `SHA256(seed + '#winner')` 에서 나온다 — 그게 결과 유출을 막는 장치이고,
   * 전송을 지우면 그 증거가 사라진다. **화면에서 안 그릴 뿐이다.**
   *
   * 빈자리는 **예측할 거리**가 채운다 — 위 출전표의 **회차 · 예상 배당 · 컨디션**.
   * 아직 없는 것은 **말별 누적 승률** 하나다. 처음엔 D1 스키마가 필요한 줄 알았는데
   * **아니다** — 계정 데이터가 아니라 **세계 데이터**라 `race.id` 가 이미 사는
   * DO 스토리지에 여섯 칸 카운터를 두면 된다. 세계 수명이 72시간이라 세계가 바뀌면
   * 승률도 같이 리셋되는 것이 오히려 맞다("제 N 세계의 누적"). 서버가 그 값을
   * 실어 보내면 여기 한 줄이다.
   */
  return h;
}

/** 트랙 — 시드 해시로 그린다. 결과가 이미 정해져 있으므로 서버가 좌표를 보낼 이유가 없다 */
function track(r, t) {
  // `run` 중에는 1등을 모른다(서버가 안 준다). 그래서 그림은 **해시가 정한 리듬**만
  // 쓰고, 결과 단계에서 승자가 확정되면 그때 결승선이 맞춰진다.
  const w = r.winner >= 0 ? r.winner : -1;
  const pos = positions(r.hash, w, t);
  let h = '<div class="track">';
  for (let i = 0; i < SLIMES; i++) {
    h += `<div class="lane"><i style="left:${(pos[i] * 88).toFixed(1)}%;background:${SLIME_COL[i]}"></i></div>`;
  }
  return h + '</div>';
}
